/* eslint-disable react/iframe-missing-sandbox */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack, Dialog, Typography, DialogContent, Tooltip, Divider, Link, CircularProgress } from '@mui/material';
import { FileOutlined } from '@ant-design/icons';

import MyChip from '../../../components/MyChip';
import TagBox from '../../../components/TagBox';
import RenderHTML from '../../../../../components/RenderHTML';
import ChatAvatarComponent from '../../chat/ChatAvatarComponent';

import { camelCaseToCapital } from '../../../../../utils/topicsMapping';
import { camelCaseToCapitalIndustries } from '../../../../../utils/industriesMapping';
import { ORFIUM } from '../../../../../utils/constants';
import { useAppSelector } from '../../../../../redux/hooks';
import CommonModalHeader from '../../../../../components/DialogUi/CommonModalHeader';
import CloseIcon from '@mui/icons-material/Close';

interface Props {
  openPreview: boolean;
  setOpenPreview: any;
  singleListData: any;
  loading?: boolean;
}

const PreviewContent = ({ openPreview, setOpenPreview, singleListData, loading }: Props) => {
  const oid = useAppSelector((state) => state.orgDetails.data?.oid);

  const tags = (singleListData?.tags ? camelCaseToCapital(singleListData.tags) : []).concat(
    singleListData?.industries ? camelCaseToCapitalIndustries(singleListData.industries) : []
  );

  const allTags = tags.join(', ');

  const [divHeight, setDivHeight] = useState<number | null>(null);
  const stackRef = useRef<HTMLDivElement>(null);

  const handleDialogClose = () => setOpenPreview(false);

  useEffect(() => {
    if (!openPreview) return;
    setTimeout(() => {
      if (stackRef.current) setDivHeight(stackRef.current.clientHeight);
    }, 0);
  }, [openPreview, singleListData]);

  const renderTags = () => (
    <Stack ref={stackRef} direction="row" useFlexGap flexWrap="wrap" spacing={1} alignItems="center">
      {tags.map((t, i) => (
        <MyChip
          key={i}
          label={t}
          backgroundColor="#2A2A2A"
          color="#FDFDFD"
          // sx={{
          //   fontSize: '12px',
          //   fontFamily: 'Open Sans',
          //   borderRadius: '20px',
          //   px: '8px',
          //   py: '3px'
          // }}
        />
      ))}
    </Stack>
  );

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      scroll="body"
      open={openPreview}
      onClose={handleDialogClose}
      PaperProps={{
        sx: {
          background: '#151515',
          borderRadius: '6px',
          overflow: 'hidden'
        }
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          width: '100%',
          px: '25px',
          py: '20px',
          borderBottom: '1px solid #2A2A2A',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          position: 'relative'
        }}
      >
        <Typography
          sx={{
            color: '#FDFDFD',
            fontSize: '18px',
            fontWeight: 700,
            fontFamily: 'Open Sans',
            flex: 1
          }}
        >
          Article View
        </Typography>

        {/* CLOSE ICON (Figma square outline) */}
        <Box
          onClick={handleDialogClose}
          sx={{
            width: 22,
            height: 22,
            position: 'absolute',
            right: 25,
            top: 20,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#FDFDFD',
            '&:hover': {
              opacity: 0.8
            }
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </Box>
      </Box>

      {/* CONTENT */}
      <DialogContent
        sx={{
          px: 0,
          py: 0,
          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        {loading ? (
          <Box
            sx={{
              width: '100%',
              minHeight: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CircularProgress size={48} sx={{ color: '#F79420' }} />
          </Box>
        ) : (
          <Box
            sx={{
              width: '760px',
              px: '30px',
              py: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '30px',
              fontFamily: "'Open Sans', sans-serif"
            }}
          >
            {/* TITLE + READ TIME */}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Typography
                  sx={{
                    color: '#868686',
                    fontSize: '12px',
                    fontWeight: 600,
                    fontFamily: 'Open Sans',
                    lineHeight: '12px'
                  }}
                >
                  {singleListData?.readTime} read
                </Typography>

                <Typography
                  sx={{
                    color: '#FDFDFD',
                    fontSize: '30px',
                    fontWeight: 600,
                    fontFamily: 'Open Sans'
                  }}
                >
                  {singleListData?.articleName}
                </Typography>
              </Box>

              {/* TAGS */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {tags.length > 0 && (
                  <Box sx={{ maxHeight: '32px', overflow: 'hidden' }}>
                    {divHeight && divHeight > 32 ? (
                      <Tooltip title={allTags} placement="top-start">
                        <Box>{renderTags()}</Box>
                      </Tooltip>
                    ) : (
                      renderTags()
                    )}
                  </Box>
                )}

                {/* AUTHOR */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <ChatAvatarComponent
                    height="30px"
                    width="30px"
                    type="noStatus"
                    image={singleListData?.coverImageUrl}
                    firstLetter={singleListData?.authorName?.charAt(0)}
                  />

                  <Typography
                    sx={{
                      color: '#FDFDFD',
                      fontSize: '16px',
                      fontWeight: 500,
                      fontFamily: 'Open Sans'
                    }}
                  >
                    {singleListData?.authorName}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ width: '100%', borderColor: '#2A2A2A' }} />

            {/* MEDIA (IMAGE / VIDEO / AUDIO) */}
            {singleListData?.type === 'Video' && (
              <iframe
                title="video"
                src={singleListData?.fileUrl}
                style={{
                  width: '560px',
                  height: '313px',
                  borderRadius: '10px',
                  border: 'none'
                }}
                allowFullScreen
              />
            )}

            {singleListData?.type === 'Audio' && (
              <audio controls style={{ width: '560px' }}>
                <source src={singleListData?.fileUrl} />
              </audio>
            )}

            {singleListData?.type === 'Text' && singleListData?.fileUrl?.includes('http') && (
              <Link
                onClick={() => window.open(singleListData.fileUrl, '_blank')}
                sx={{ color: '#FDFDFD', fontSize: '14px', cursor: 'pointer', fontFamily: 'Open Sans' }}
              >
                <FileOutlined /> {singleListData.articleName}
              </Link>
            )}

            {/* BODY TEXT */}
            <Box
              sx={{
                width: '700px',
                color: '#B6B6B6',
                fontSize: '14px',
                fontFamily: 'Open Sans',
                lineHeight: '24px'
              }}
            >
              <RenderHTML htmlContent={singleListData?.articleDescription} />
            </Box>

            {/* EMBEDDED LINKS */}
            {singleListData?.embeddedLinks && (
              <Box sx={{ width: '100%', mt: 2 }}>
                {singleListData.embeddedLinks.split(',').map((link: string, i: number) => {
                  const clean = link.trim();
                  const ext = clean.split('.').pop()?.toLowerCase();

                  if (['jpg', 'jpeg', 'png'].includes(ext || '')) {
                    return (
                      <img
                        key={i}
                        src={clean}
                        style={{
                          width: '560px',
                          borderRadius: '10px'
                        }}
                      />
                    );
                  }

                  return (
                    <iframe
                      key={i}
                      src={clean}
                      style={{
                        width: '560px',
                        height: '313px',
                        borderRadius: '10px'
                      }}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PreviewContent;
